import React, { useState, useEffect, useMemo } from 'react'
import ReactDOM from 'react-dom'
import TradingCard, { CardData } from './TradingCard'
import { useAuth } from '@/contexts/AuthContext'
import { useIsMobile } from '@/hooks/useIsMobile'
import { CandidateProjection, projectionPillStyle, useCardProjection } from '@/hooks/useCardProjection'
import {
  CardFilterState, defaultCardFilterState, applyCardFilters, CardFilterControls,
} from './cardFilters'

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api'

const PickerCard: React.FC<{
  card: CardData
  isMatch: boolean
  // Effect is already equipped in another slot — only one of each effect
  // can be equipped at a time, so this card can't be picked.
  isDuplicateEffect?: boolean
  // Fusion: this card's player is already fielded in another slot (no player
  // can fill two slots), so it can't be picked.
  isDuplicatePlayer?: boolean
  onSelect: (card: CardData) => void
  projection?: CandidateProjection
}> = ({ card, isMatch, isDuplicateEffect, isDuplicatePlayer, onSelect, projection }) => {
  const [hovered, setHovered] = useState(false)
  const style = projection ? projectionPillStyle(projection) : null
  const disabled = !!isDuplicateEffect || !!isDuplicatePlayer
  const badgeText = isDuplicatePlayer ? 'In Lineup' : 'Equipped Elsewhere'
  const disabledTitle = isDuplicatePlayer
    ? 'This player is already in your lineup'
    : 'This effect is already equipped in another slot'
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
        transition: 'transform 0.15s',
        transform: hovered && !disabled ? 'translateY(-4px)' : 'none',
      }}
    >
      <div style={{
        position: 'relative',
        boxShadow: isMatch && !disabled ? '0 0 0 2px #60a5fa, 0 0 12px rgba(96,165,250,0.3)' : 'none',
      }}>
        {/* Apply the disabled wash to JUST the card art so the
            EQUIPPED ELSEWHERE badge stays fully vivid on top. */}
        <div style={{
          opacity: disabled ? 0.4 : 1,
          filter: disabled ? 'grayscale(0.7)' : 'none',
          transition: 'opacity 0.15s, filter 0.15s',
        }}>
          <TradingCard
            card={card}
            size="sm"
            noHoverLift
            onHoverChange={disabled ? () => {} : setHovered}
          />
        </div>
        {isMatch && !disabled && (
          <div style={{
            position: 'absolute', top: 4, left: '50%', transform: 'translateX(-50%)',
            fontSize: '9px', color: '#60a5fa', fontWeight: '700',
            backgroundColor: 'rgba(96,165,250,0.15)',
            padding: '2px 5px',
            border: '1px solid rgba(96,165,250,0.3)',
            zIndex: 1,
          }}>
            MATCH
          </div>
        )}
        {disabled && (
          <div style={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: '12px', color: '#0f172a', fontWeight: '800',
            backgroundColor: '#fbbf24',
            padding: '6px 12px',
            border: '2px solid #f59e0b',
            boxShadow: '0 4px 14px rgba(0,0,0,0.5), 0 0 0 4px rgba(251,191,36,0.25)',
            zIndex: 2, whiteSpace: 'nowrap' as const,
            letterSpacing: '0.5px',
            textTransform: 'uppercase' as const,
            pointerEvents: 'none' as const,
          }}>
            {badgeText}
          </div>
        )}
      </div>
      {/* Projection effectiveness chip — placed between card and equip
          button so it's legible without fighting the card art. */}
      {projection && style && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
          <span
            style={{
              display: 'inline-flex', alignItems: 'center',
              fontSize: '12px', fontWeight: 700,
              color: style.color, backgroundColor: style.bg,
              padding: '4px 10px',
              border: `1px solid ${style.color}55`,
              fontVariantNumeric: 'tabular-nums' as const,
              whiteSpace: 'nowrap' as const,
            }}
          >
            {style.label}
          </span>
          {style.ceiling && (
            <span style={{
              fontSize: '10px', color: style.color, opacity: 0.8,
              fontVariantNumeric: 'tabular-nums' as const,
              whiteSpace: 'nowrap' as const,
            }}>
              {style.ceiling}
            </span>
          )}
        </div>
      )}
      <button
        onClick={() => !disabled && onSelect(card)}
        disabled={disabled}
        title={disabled ? disabledTitle : undefined}
        style={{
          backgroundColor: disabled ? '#1e293b' : 'rgba(59,130,246,0.85)',
          border: `1px solid ${disabled ? '#334155' : 'rgba(96,165,250,0.5)'}`,
          color: disabled ? '#64748b' : '#fff',
          fontSize: '10px', fontWeight: '700',
          fontFamily: 'pressStart',
          padding: '5px 14px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'background-color 0.15s',
        }}
      >
        {disabled ? 'In use' : 'Equip'}
      </button>
    </div>
  )
}

interface CardPickerModalProps {
  visible: boolean
  onClose: () => void
  onSelect: (card: CardData) => void
  excludeCardIds: number[]  // user_card IDs already equipped in other slots
  // effectNames already equipped in OTHER slots. Cards with these
  // effectNames stay visible in the picker but render as "EQUIPPED
  // ELSEWHERE" + disabled Equip button, so the no-duplicate rule is
  // visible at choose-time instead of via a 400 on save.
  excludeEffectNames?: string[]
  // Fusion: user_card players already fielded in other slots — no player can
  // fill two slots, so these render disabled ("In Lineup").
  excludePlayerIds?: number[]
  rosterPlayerIds: Set<number>
  // Slot the picker is scoped to. Candidate projections are computed
  // as if each card replaced whatever currently occupies this slot —
  // so FPx chain changes, chance-synergy changes, and straight-swap
  // deltas all land accurately.
  targetSlot?: number | null
  // Fusion position-locked picker: restrict to a position (1-based; null/undef =
  // any, e.g. FLEX), label the header with the slot, and drop the match/position
  // controls that don't apply.
  position?: number | null
  slotLabel?: string
  slotScoped?: boolean
  // Teams of cards already in the lineup — enables a "same team as lineup" filter so users
  // can build the same-team stack (the team-color glow + team-stacking FPx synergy).
  equippedTeamIds?: Set<number>
}

const CardPickerModal: React.FC<CardPickerModalProps> = ({
  visible, onClose, onSelect, excludeCardIds, excludeEffectNames, excludePlayerIds,
  rosterPlayerIds, targetSlot, position, slotLabel, slotScoped, equippedTeamIds,
}) => {
  const excludedEffectSet = useMemo(
    () => new Set(excludeEffectNames ?? []),
    [excludeEffectNames],
  )
  const excludedPlayerSet = useMemo(
    () => new Set(excludePlayerIds ?? []),
    [excludePlayerIds],
  )
  const { getToken } = useAuth()
  // Only fetch candidate projections while the modal is actually open.
  const { candidatesByUserCardId } = useCardProjection(visible, visible ? (targetSlot ?? null) : null)
  const isMobile = useIsMobile()
  const [cards, setCards] = useState<CardData[]>([])
  // ⚠️ THE POOL IS A SEPARATE SECTION, NOT MORE CARDS IN THE SAME LIST. Every player's
  // floor print is available to everybody, so merging them in would bury the cards a
  // user actually pulled under ~190 identical no-effect prints — which is the same
  // reason the collection endpoint filters `edition == 'base'` out server-side.
  const [pool, setPool] = useState<CardData[]>([])
  const [showPool, setShowPool] = useState(false)
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState<CardFilterState>(defaultCardFilterState)
  const patchFilters = (patch: Partial<CardFilterState>) => setFilters(f => ({ ...f, ...patch }))
  const [myTeamsOnly, setMyTeamsOnly] = useState(false)

  // Reset controls whenever the modal opens so previous filters don't stick.
  // Slot-scoped (fusion) pickers default to highest-rated since match sort is gone.
  useEffect(() => {
    if (visible) { setMyTeamsOnly(false); setFilters({ ...defaultCardFilterState, sort: slotScoped ? 'rating' : 'match' }) }
  }, [visible, slotScoped])

  useEffect(() => {
    if (!visible) return
    setLoading(true)
    const fetchCards = async () => {
      try {
        const tok = await getToken()
        if (!tok) return
        const posParam = position != null ? `&position=${position}` : ''
        const res = await fetch(`${API_BASE}/cards/collection?activeOnly=true&vaulted=false&equipped=false${posParam}`, {
          headers: { Authorization: `Bearer ${tok}` },
        })
        if (!res.ok) return
        const json = await res.json()
        const all: CardData[] = json.data?.cards ?? []
        setCards(all.filter(c => !excludeCardIds.includes(c.id)))
      } catch {
        setCards([])
      } finally {
        setLoading(false)
      }
    }
    const fetchPool = async () => {
      try {
        const tok = await getToken()
        if (!tok) return
        const posQ = position != null ? `?position=${position}` : ''
        const res = await fetch(`${API_BASE}/cards/base-pool${posQ}`, {
          headers: { Authorization: `Bearer ${tok}` },
        })
        if (!res.ok) return
        const json = await res.json()
        // ⚠️ Shaped into a CardData so ONE card component renders both sections, but with
        // `id: 0` — a pool card genuinely has no `UserCard` row, and giving it a fake id
        // would let it be equipped as a card that does not exist. `fromPool` is what the
        // equip path keys on to send `templateId` instead.
        const raw = (json.data?.cards ?? json.cards ?? []) as any[]
        setPool(raw.map(c => ({
          id: 0,
          templateId: c.templateId,
          playerId: c.playerId,
          playerName: c.playerName,
          teamId: c.teamId ?? null,
          teamColor: null,
          playerRating: c.playerRating,
          position: c.position,
          edition: 'base',
          seasonCreated: json.data?.season ?? json.season ?? 0,
          isRookie: false,
          effectConfig: {},
          effectName: 'none',
          sellValue: 2,
          isActive: true,
          fromPool: true,
        }) as CardData))
      } catch {
        setPool([])
      }
    }
    fetchCards()
    fetchPool()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, getToken, excludeCardIds.join(','), position])

  useEffect(() => {
    if (!visible) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [visible, onClose])

  // Apply search + filters, then sort (shared engine). useMemo so we only
  // recompute when the cards, filter state, or roster change.
  const displayed = useMemo(() => {
    let list = applyCardFilters(showPool ? pool : cards, filters, rosterPlayerIds)
    if (myTeamsOnly && equippedTeamIds && equippedTeamIds.size) {
      list = list.filter(c => c.teamId != null && equippedTeamIds.has(c.teamId))
    }
    return list
  }, [cards, pool, showPool, filters, rosterPlayerIds, myTeamsOnly, equippedTeamIds])

  if (!visible) return null

  const totalCount = cards.length
  const matchingCount = cards.filter(c => rosterPlayerIds.has(c.playerId)).length

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10002,
        backgroundColor: 'rgba(0,0,0,0.8)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%', maxWidth: '700px',
          // Fixed height so the modal doesn't jump around as filters change
          // the card count. Card grid inside handles its own scrolling.
          height: isMobile ? '92vh' : '85vh',
          backgroundColor: '#1e293b', border: '1px solid #334155', boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
          fontFamily: 'pressStart', display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #475569', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#f1f5f9', marginBottom: '2px' }}>
                {slotLabel ? `Fill ${slotLabel}` : 'Select Card'}
              </div>
              <div style={{ fontSize: '10px', color: '#94a3b8' }}>
                {showPool
                  ? `${displayed.length} of ${pool.length} players`
                  : `${displayed.length} of ${totalCount} cards`}
                {!showPool && matchingCount > 0 && `, ${matchingCount} match roster`}
              </div>
            </div>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '22px', padding: '2px 6px' }}
            >x</button>
          </div>

          {/* Two sections: the cards you own, and every player in the league. The pool
              is not inventory — a floor print is available to everybody and is only
              turned into an owned card at the moment it is fielded — so it gets its own
              tab rather than being mixed into a collection it would swamp. */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
            {([[false, 'My Cards'], [true, 'All Players']] as [boolean, string][]).map(
              ([val, label]) => (
                <button
                  key={label}
                  onClick={() => setShowPool(val)}
                  style={{
                    flex: 1,
                    backgroundColor: showPool === val ? 'rgba(59,130,246,0.85)' : '#0f172a',
                    border: `1px solid ${showPool === val ? 'rgba(96,165,250,0.5)' : '#334155'}`,
                    color: showPool === val ? '#fff' : '#94a3b8',
                    fontFamily: 'pressStart', fontSize: '10px', fontWeight: 700,
                    padding: '7px 10px', cursor: 'pointer',
                    transition: 'background-color 0.15s, color 0.15s',
                  }}
                >{label}</button>
              ))}
          </div>
          {/* Shared equip-side filter bar (search + pills + sort/match toggle).
              Slot-scoped fusion pickers drop the position + match controls. */}
          <CardFilterControls
            state={filters} onPatch={patchFilters}
            showPosition={!slotScoped}
            showMatchToggle={!slotScoped}
            showMatchSort={!slotScoped}
          />
          {equippedTeamIds && equippedTeamIds.size > 0 && (
            <button
              onClick={() => setMyTeamsOnly(v => !v)}
              style={{
                marginTop: '8px', fontSize: '10px', fontWeight: '700', fontFamily: 'pressStart',
                padding: '5px 11px', cursor: 'pointer',
                border: `1px solid ${myTeamsOnly ? '#38bdf8' : '#475569'}`,
                backgroundColor: myTeamsOnly ? 'rgba(56,189,248,0.16)' : 'transparent',
                color: myTeamsOnly ? '#7dd3fc' : '#94a3b8',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              <span style={{ fontSize: '12px' }}>◈</span>
              Same team as lineup
            </button>
          )}
        </div>

        {/* Card grid */}
        <div style={{ overflowY: 'auto', padding: '16px' }}>
          {loading ? (
            <div style={{ padding: '32px', textAlign: 'center', fontSize: '13px', color: '#94a3b8' }}>Loading...</div>
          ) : totalCount === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', fontSize: '13px', color: '#94a3b8' }}>
              No active cards available. Open packs in the Shop!
            </div>
          ) : displayed.length === 0 ? (
            <div style={{ padding: '32px', textAlign: 'center', fontSize: '13px', color: '#94a3b8' }}>
              No cards match your filters.
            </div>
          ) : (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '12px',
              justifyContent: isMobile ? 'center' : 'flex-start',
            }}>
              {displayed.map(card => {
                const isMatch = rosterPlayerIds.has(card.playerId)
                const isDuplicatePlayer = excludedPlayerSet.has(card.playerId)
                const isDuplicateEffect = !!card.effectName && card.effectName !== 'none'
                  && excludedEffectSet.has(card.effectName)
                return (
                  /* ⚠️ POOL CARDS ARE ALL `id: 0` — deliberately, since a pool card has no
                     UserCard row — so keying on `card.id` gave every one of them the SAME
                     React key. React reconciles by key, so switching from All Players back
                     to My Cards reused those mounted components and the base players stayed
                     on screen inside the collection list. Reported from the app.
                     A pool card is identified by its TEMPLATE, an owned card by its id, and
                     the prefixes stop the two id spaces colliding. */
                  <PickerCard
                    key={card.fromPool ? `t${card.templateId}` : `c${card.id}`}
                    card={card} isMatch={isMatch}
                    isDuplicateEffect={isDuplicateEffect}
                    isDuplicatePlayer={isDuplicatePlayer}
                    onSelect={onSelect}
                    projection={candidatesByUserCardId.get(card.id)}
                  />
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

export default CardPickerModal
